import { useState, useEffect } from "react";
import { Building, MapPin, Calendar, Layers, Lock, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Slider } from "../ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { deriveTimeline } from "./quoteMapping";

const SERVICE_TYPES = ["Renderings", "Marketing", "Both"];

// Default weeks by service type (manual mode only)
const DEFAULT_WEEKS = {
  "Renderings": 8,
  "Marketing": 6,
  "Both": 10
};

function addWeeks(dateStr, weeks) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  date.setDate(date.getDate() + (weeks * 7));
  return date.toISOString().split('T')[0];
}

function formatDateDisplay(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function IntakeProjectStep({ form, setForm, errors, onNext, onBack, quoteMode = false, sourceQuote = null }) {
  const [deliveryWeeks, setDeliveryWeeks] = useState(form.delivery_weeks || DEFAULT_WEEKS[form.service_type] || 8);

  // ── Quote mode: delivery is derived from the quote's parallel timeline ──
  const tl = quoteMode ? deriveTimeline(sourceQuote, form.start_date) : null;

  useEffect(() => {
    if (!quoteMode) return;
    if (!form.start_date) return;
    // VEXOS ∥ Visualization run in parallel → total is the max track. If the
    // quote has no timeline snapshot, fall back to an 8-week default so the
    // delivery date is still populated.
    const weeks = tl?.totalWeeks || 8;
    const deliveryDate = tl?.deliveryDate || addWeeks(form.start_date, weeks);
    if (form.expected_delivery_date !== deliveryDate || form.delivery_weeks !== weeks) {
      setForm(prev => ({ ...prev, expected_delivery_date: deliveryDate, delivery_weeks: weeks }));
    }
  }, [quoteMode, form.start_date, tl?.totalWeeks, tl?.deliveryDate]);

  // ── Manual mode: weeks slider drives the delivery date ──
  useEffect(() => {
    if (quoteMode) return;
    if (form.start_date) {
      const newDeliveryDate = addWeeks(form.start_date, deliveryWeeks);
      setForm(prev => ({
        ...prev,
        expected_delivery_date: newDeliveryDate,
        delivery_weeks: deliveryWeeks
      }));
    }
  }, [quoteMode, form.start_date, deliveryWeeks]);

  useEffect(() => {
    if (quoteMode) return;
    if (!form.delivery_weeks) {
      const defaultWeeks = DEFAULT_WEEKS[form.service_type] || 8;
      setDeliveryWeeks(defaultWeeks);
    }
  }, [quoteMode, form.service_type]);

  return (
    <Card>
      <CardHeader className="px-4 sm:px-6">
        <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
          <Building className="w-5 h-5" />
          Project Information
        </CardTitle>
        <CardDescription className="text-sm">Enter the project details and timeline</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 px-4 sm:px-6">
        <div className="space-y-1.5">
          <Label className="text-sm">Project Name *</Label>
          <Input
            value={form.project_name}
            onChange={e => setForm({ ...form, project_name: e.target.value })}
            placeholder="Riverside Luxury Condos"
            className={`h-10 sm:h-11 ${errors.project_name ? "border-red-500" : ""}`}
            autoFocus
          />
          {errors.project_name && <p className="text-xs text-red-500">{errors.project_name}</p>}
        </div>

        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <div className="space-y-1.5">
            <Label className="text-sm">City *</Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={form.project_city}
                onChange={e => setForm({ ...form, project_city: e.target.value })}
                placeholder="Miami"
                className={`pl-10 h-10 sm:h-11 ${errors.project_city ? "border-red-500" : ""}`}
              />
            </div>
            {errors.project_city && <p className="text-xs text-red-500">{errors.project_city}</p>}
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm">State *</Label>
            <Input
              value={form.project_state}
              onChange={e => setForm({ ...form, project_state: e.target.value })}
              placeholder="FL"
              maxLength={2}
              className={`h-10 sm:h-11 ${errors.project_state ? "border-red-500" : ""}`}
            />
            {errors.project_state && <p className="text-xs text-red-500">{errors.project_state}</p>}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-sm">Project Address</Label>
          <Input
            value={form.project_address_full || ""}
            onChange={e => setForm({ ...form, project_address_full: e.target.value })}
            placeholder="123 Main St, Suite 100"
            className="h-10 sm:h-11"
            data-testid="project-address-input"
          />
          <p className="text-xs text-muted-foreground">Full street address (optional)</p>
        </div>

        {/* Service Type — locked descriptor in quote mode, dropdown in manual mode */}
        {quoteMode ? (
          <div className="space-y-1.5">
            <Label className="text-sm flex items-center gap-2">
              Service Type
              <Badge variant="secondary" className="text-[10px] font-normal gap-1"><Lock className="w-2.5 h-2.5" /> from quote</Badge>
            </Label>
            <div className="flex items-center gap-2 rounded-lg border bg-muted/60 px-3 h-10 sm:h-11">
              <Layers className="w-4 h-4 text-primary flex-shrink-0" />
              <span className="text-sm font-medium">{form.service_summary || form.service_type}</span>
            </div>
            <p className="text-xs text-muted-foreground">Determined by the quote's scope.</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            <Label className="text-sm">Service Type *</Label>
            <Select value={form.service_type} onValueChange={v => setForm({ ...form, service_type: v })}>
              <SelectTrigger className="h-10 sm:h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SERVICE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Start Date */}
        <div className="space-y-1.5">
          <Label className="text-sm">Start Date *</Label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="date"
              value={form.start_date}
              onChange={e => setForm({ ...form, start_date: e.target.value })}
              className={`pl-10 h-10 sm:h-11 ${errors.start_date ? "border-red-500" : ""}`}
            />
          </div>
          {errors.start_date && <p className="text-xs text-red-500">{errors.start_date}</p>}
        </div>

        {/* Estimated Delivery */}
        {quoteMode ? (
          <div className="space-y-3 p-4 bg-secondary/30 rounded-lg">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Clock className="w-4 h-4" /> Estimated Delivery
              </Label>
              <Badge variant="secondary" className="text-[10px] font-normal gap-1"><Lock className="w-2.5 h-2.5" /> from quote</Badge>
            </div>

            {tl?.hasTimeline ? (
              <div className="space-y-1.5 text-sm">
                {tl.vexosWeeksDisplay && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">VEXOS platform</span>
                    <span className="font-medium">{tl.vexosWeeksDisplay}</span>
                  </div>
                )}
                {tl.vizWeeksDisplay && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Visualization</span>
                    <span className="font-medium">{tl.vizWeeksDisplay}</span>
                  </div>
                )}
                <p className="text-[11px] text-muted-foreground pt-0.5">
                  These run in parallel — the total is the longer of the two tracks, not the sum.
                </p>
                <div className="flex items-center justify-between pt-2 border-t">
                  <span className="font-medium">Total timeline</span>
                  <Badge variant="outline" className="text-sm font-semibold">~{tl.totalWeeks} weeks</Badge>
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                This quote has no saved timeline — defaulting to ~8 weeks. Adjust after activation if needed.
              </p>
            )}

            {form.start_date && (
              <div className="flex items-center justify-between pt-2 border-t">
                <span className="text-sm text-muted-foreground">Estimated Delivery Date:</span>
                <span className="text-sm font-medium">{formatDateDisplay(form.expected_delivery_date)}</span>
              </div>
            )}
            {errors.expected_delivery_date && <p className="text-xs text-red-500">{errors.expected_delivery_date}</p>}
          </div>
        ) : (
          <div className="space-y-3 p-4 bg-secondary/30 rounded-lg">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Estimated Delivery</Label>
              <Badge variant="outline" className="text-sm font-semibold">
                {deliveryWeeks} weeks
              </Badge>
            </div>

            <Slider
              value={[deliveryWeeks]}
              onValueChange={([value]) => setDeliveryWeeks(value)}
              min={2}
              max={16}
              step={1}
              className="py-2"
            />

            <div className="flex justify-between text-xs text-muted-foreground">
              <span>2 weeks</span>
              <span>16 weeks</span>
            </div>

            {form.start_date && (
              <div className="flex items-center justify-between pt-2 border-t">
                <span className="text-sm text-muted-foreground">Estimated Delivery Date:</span>
                <span className="text-sm font-medium">
                  {formatDateDisplay(form.expected_delivery_date)}
                </span>
              </div>
            )}

            {errors.expected_delivery_date && <p className="text-xs text-red-500">{errors.expected_delivery_date}</p>}
          </div>
        )}
      </CardContent>
      <CardFooter className="flex flex-col sm:flex-row justify-between gap-2 px-4 sm:px-6 pb-4 sm:pb-6">
        <Button variant="outline" onClick={onBack} className="w-full sm:w-auto h-10 sm:h-11">Back</Button>
        <Button onClick={onNext} className="w-full sm:w-auto h-10 sm:h-11">Next: Links & Scope</Button>
      </CardFooter>
    </Card>
  );
}
